import { useEffect, useState } from 'react';

/** The breakpoint every stylesheet in the app already uses for "one pane". */
export const MOBILE_QUERY = '(max-width: 720px)';

/** A finger rather than a mouse, at any width — see useIsTouch. */
export const TOUCH_QUERY = '(pointer: coarse)';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);

  return matches;
}

/**
 * The same breakpoint the CSS branches on, read from JS.
 *
 * Two behaviours genuinely differ rather than merely looking different, so a
 * media query alone cannot express them: the smiley panel replaces the system
 * keyboard instead of floating over the thread, and Enter inserts a line break
 * instead of sending. Both are decisions the component has to make, not styles.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_QUERY);
}

/**
 * Whether this screen is touched rather than pointed at — a different question
 * from the breakpoint above, and one a tablet answers the other way round.
 *
 * An iPad is 810px across in portrait and half as much again in landscape, so
 * the breakpoint calls it a desktop, lays the app out in two panes and is right
 * to. What does not follow is that it should be denied press-and-hold
 * recording: it has a camera, a microphone and a thumb, and nothing else in the
 * composer to record with. `pointer: coarse` asks about the thumb, and still
 * says no on a laptop whose screen happens to be a touchscreen, because its
 * primary pointer is the mouse.
 */
export function useIsTouch(): boolean {
  return useMediaQuery(TOUCH_QUERY);
}
